import { useMutation } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { isSupabaseConfigured, supabase } from "@/lib/supabaseClient";
import type { SelectedItems } from "@/types/recipe";

const unwrapOutput = (parsed: any) => {
  if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.output) {
    return parsed[0].output;
  }
  if (parsed && parsed.output) {
    return parsed.output;
  }
  return parsed;
};

export const useRecipeMutation = (onSettled: () => void) =>
  useMutation({
    mutationFn: async (payload: SelectedItems) => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (isSupabaseConfigured && supabase) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          headers["Authorization"] = `Bearer ${session.access_token}`;
        }
      }

      const response = await fetch("/api/recipe", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const raw = await response.text();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.error("Auth error from server:", raw);
          throw new Error("auth_error");
        }
        console.error("Server error:", raw);
        throw new Error("server_error");
      }

      try {
        return unwrapOutput(JSON.parse(raw));
      } catch {
        return { text: raw };
      }
    },
    onSettled,
    onSuccess: () => {
      toast({ title: "Success!", description: "Your recipe has been generated." });
    },
    onError: (error: Error) => {
      if (error.message === "auth_error") {
        toast({
          title: "Unauthorized",
          description: "Your session may have expired. Please sign in again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to generate recipe. Please try again.",
          variant: "destructive",
        });
      }
    },
  });
